import type { AccountInfoData } from "../AccountSelectionTabelView/api/GetAccountList";
import { useEffect, useMemo, useState } from "react";
import AccountSelectionGrid from "./components/AccountSelectionGrid";
import AccountSelectionSearch from "./components/AccountSelectionSearch";
import { AccountSelectionBottomButton, setAccount } from "../AccountSelectionPage";
import { makeStyles } from "@mui/styles";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { useTranslation } from "react-i18next";

interface NewAccountSelectionGridProps {
  accounts: AccountInfoData[];
  selectedAccountId: string;
  className?: string;
}

const useStyles = makeStyles({
  button: { borderRadius: "100px !important", marginTop: "32px !important", padding: "10px 100px !important" },
});

const NewAccountSelectionGrid = ({ accounts, selectedAccountId, className }: NewAccountSelectionGridProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [selectedAccount, setSelectedAccount] = useState<AccountInfoData | undefined>();

  const styles = useStyles();

  useEffect(() => {
    setSelectedAccount(accounts.find((account) => account.id === selectedAccountId));
  }, [accounts, selectedAccountId]);

  const filteredAccounts = useMemo(() => {
    if (!accounts) {
      return [];
    }

    return accounts.filter((account) => {
      const query = value.toLowerCase();
      const filteredValue = `${account.name}-${account.number}`.toLowerCase();
      return filteredValue.includes(query);
    });
  }, [accounts, value]);

  const buttonDisabled =
    filteredAccounts.findIndex((account) => account === selectedAccount) === -1 || !selectedAccount;

  const isDesktop = useIsMinDesktopSize();

  const handleAccountChange = () => setAccount(selectedAccount!.uid, selectedAccount?.salesOrg);

  const handleOnAccountClick = (account: AccountInfoData) => {
    setSelectedAccount(account);
    if (isDesktop) {
      setAccount(account.uid, account.salesOrg);
    }
  };

  return (
    <>
      <AccountSelectionSearch value={value} onChange={(e) => setValue(e.target.value)} />
      <AccountSelectionGrid
        selectedAccount={selectedAccount}
        onAccountClick={handleOnAccountClick}
        accounts={filteredAccounts}
      />

      {!isDesktop && <AccountSelectionBottomButton onClick={handleAccountChange} disabled={buttonDisabled} />}
    </>
  );
};

export default NewAccountSelectionGrid;
